import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  ActivityIndicator,
  Pressable,
  Keyboard,
} from 'react-native';
import Modal from 'react-native-modal';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import { usernameSetupModalVisibleAtom } from '../atoms';
import {
  validateUsername,
  checkUsernameAvailability,
  setUsername,
} from '../services/usernameService';
import PixelContainer from './PixelContainer';
import { fontStyle } from '../config/fonts';

interface UsernameSetupModalProps {
  userId: string;
  currentUsername?: string;
  onUsernameSet: (username: string) => void;
  isFirstTime?: boolean;
}

export default function UsernameSetupModal({
  userId,
  currentUsername,
  onUsernameSet,
  isFirstTime = false,
}: UsernameSetupModalProps) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isVisible, setIsVisible] = useAtom(usernameSetupModalVisibleAtom);
  const [username, setUsernameInput] = useState(currentUsername || '');
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isVisible) {
      setUsernameInput(currentUsername || '');
      setValidationError(null);
      setIsAvailable(null);
    }
  }, [isVisible, currentUsername]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Debounced availability check
  const checkAvailability = useCallback(
    (value: string) => {
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (!value || value.length < 3) {
        setIsAvailable(null);
        setIsChecking(false);
        return;
      }

      const validation = validateUsername(value);
      if (!validation.isValid) {
        setValidationError(validation.error || null);
        setIsAvailable(null);
        setIsChecking(false);
        return;
      }

      // Set checking state immediately for UI feedback
      setIsChecking(true);

      // Debounce the actual API call
      debounceTimeoutRef.current = setTimeout(async () => {
        const result = await checkUsernameAvailability(value, userId);
        setIsChecking(false);
        setIsAvailable(result.isAvailable);
        if (!result.isAvailable) {
          setValidationError(result.error || t('username.taken'));
        }
      }, 500);
    },
    [userId, t]
  );

  const handleUsernameChange = (value: string) => {
    // Only allow valid characters and lowercase
    const sanitized = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setUsernameInput(sanitized);
    setValidationError(null);
    setIsAvailable(null);

    // Validate format immediately
    if (sanitized.length > 0) {
      const validation = validateUsername(sanitized);
      if (!validation.isValid) {
        setValidationError(validation.error || null);
      } else {
        checkAvailability(sanitized);
      }
    }
  };

  const handleSave = async () => {
    if (!username || isSaving || !isAvailable) return;

    Keyboard.dismiss();
    setIsSaving(true);

    const result = await setUsername(userId, username, currentUsername);

    setIsSaving(false);

    if (result.success) {
      onUsernameSet(username.toLowerCase());
      setIsVisible(false);
    } else {
      setValidationError(result.error || t('errors.saveFailed'));
    }
  };

  const canSave = username.length >= 3 && isAvailable && !isChecking && !isSaving;

  // Don't allow dismissing if it's the first time (mandatory)
  const handleBackdropPress = () => {
    if (!isFirstTime) {
      setIsVisible(false);
    }
  };

  const handleSwipeComplete = () => {
    if (!isFirstTime) {
      setIsVisible(false);
    }
  };

  return (
    <Modal
      isVisible={isVisible}
      onBackdropPress={handleBackdropPress}
      onSwipeComplete={handleSwipeComplete}
      swipeDirection={isFirstTime ? undefined : ['down']}
      style={styles.modal}
      backdropOpacity={0.5}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      avoidKeyboard
    >
      <View style={[styles.content, { backgroundColor: colors.background }]}>
        <View style={[styles.indicator, { backgroundColor: colors.border }]} />

        <Text style={[styles.title, fontStyle, { color: colors.text }]}>
          {isFirstTime ? t('username.setupTitle') : t('username.changeTitle')}
        </Text>

        <Text style={[styles.description, fontStyle, { color: colors.textSecondary }]}>
          {isFirstTime ? t('username.setupDescription') : t('username.changeDescription')}
        </Text>

        <View style={styles.inputContainer}>
          <View
            style={[
              styles.inputWrapper,
              { backgroundColor: colors.surface, borderColor: colors.border },
              validationError && styles.inputError,
              isAvailable && styles.inputSuccess,
            ]}
          >
            <Text style={[styles.prefix, fontStyle, { color: colors.textSecondary }]}>@</Text>
            <TextInput
              style={[styles.input, fontStyle, { color: colors.text }]}
              value={username}
              onChangeText={handleUsernameChange}
              placeholder={t('username.placeholder')}
              placeholderTextColor={colors.textSecondary}
              autoCapitalize="none"
              autoCorrect={false}
              maxLength={20}
              autoFocus
            />
            {isChecking && <ActivityIndicator size="small" color={colors.textSecondary} />}
            {!isChecking && isAvailable === true && (
              <Ionicons name="checkmark-circle" size={20} color="#10B981" />
            )}
            {!isChecking && isAvailable === false && (
              <Ionicons name="close-circle" size={20} color="#EF4444" />
            )}
          </View>

          {validationError && (
            <Text style={[styles.errorText, fontStyle]}>{validationError}</Text>
          )}

          <Text style={[styles.hint, fontStyle, { color: colors.textSecondary }]}>
            {t('username.hint')}
          </Text>
        </View>

        <Pressable onPress={handleSave} disabled={!canSave}>
          <PixelContainer
            backgroundColor={canSave ? '#85c363' : colors.border}
            cornerSize={4}
            style={styles.saveButton}
          >
            {isSaving ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text style={[styles.saveButtonText, fontStyle]}>
                {isFirstTime ? t('username.continue') : t('common.save')}
              </Text>
            )}
          </PixelContainer>
        </Pressable>

        {!isFirstTime && (
          <Pressable onPress={() => setIsVisible(false)} style={styles.cancelButton}>
            <Text style={[styles.cancelText, fontStyle, { color: colors.textSecondary }]}>
              {t('common.cancel')}
            </Text>
          </Pressable>
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-end',
    margin: 0,
  },
  content: {
    padding: 24,
    paddingTop: 12,
    paddingBottom: 40,
  },
  indicator: {
    width: 40,
    height: 4,
    alignSelf: 'center',
    marginBottom: 16,
    borderRadius: 2,
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  description: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 20,
  },
  inputContainer: {
    marginBottom: 24,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 48,
  },
  inputError: {
    borderColor: '#EF4444',
  },
  inputSuccess: {
    borderColor: '#10B981',
  },
  prefix: {
    fontSize: 16,
    marginRight: 4,
  },
  input: {
    flex: 1,
    fontSize: 16,
    height: '100%',
  },
  errorText: {
    fontSize: 13,
    color: '#EF4444',
    marginTop: 8,
  },
  hint: {
    fontSize: 12,
    marginTop: 8,
  },
  saveButton: {
    padding: 14,
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  cancelButton: {
    marginTop: 16,
    alignItems: 'center',
  },
  cancelText: {
    fontSize: 14,
  },
});
